import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '@global/Api';

import { UPDATE_BILLING_INFO_MUTATION } from '@pages/SubscriptionDetails/components/ChangeBillingInfoForm/queries';

import { RootState } from '@global/store/store';
import { BillingInfoFormValuesType } from '@global/components/BillingInfoForm/types';
import { constructBillingInfo } from '@pages/checkout/components/BillingInformation/helper';
import {
  setBillingInfo,
  setNotification,
  setError,
  setIsButtonloading,
  setIsClicked,
} from '@global/store/slices/subscription/subscriptionSlice';
import { IBillingInfo } from '@global/types/types';
import { getLocaleConfig } from '@src/global/localization/services';

interface SubscriptionUpdateBillingInfoProps {
  subscriptionUid: string;
  paymentUid: string;
  billingInfo: IBillingInfo;
}

export const updateBillingInformation = createAsyncThunk<
  string,
  {
    cb?: () => void;
    t: (arg: string) => string;
  } & BillingInfoFormValuesType &
    SubscriptionUpdateBillingInfoProps,
  { rejectValue: string }
>(
  'subscription/updateBillingInformation',
  async (
    { t, cb, subscriptionUid, paymentUid, billingInfo, ...formData },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const { country } = getState() as RootState;

      const dataBillingInfo = constructBillingInfo(
        billingInfo,
        formData,
        country.countries
      );
      const { stateDropdown } = getLocaleConfig(
        dataBillingInfo?.country?.iso2Code?.toLowerCase()
      );

      const address = {
        state: stateDropdown ? dataBillingInfo.state : '',
        addressLine1: dataBillingInfo.addressOne,
        addressLine2: dataBillingInfo.addressTwo,
        zip: dataBillingInfo.zip,
        city: dataBillingInfo.city,
        phone: dataBillingInfo.tel,
        countryName: dataBillingInfo.country.name,
        countryCode: dataBillingInfo.country.numericCode,
        countryISO2: dataBillingInfo.country.iso2Code,
      };

      const mutationVariables = {
        subscriptionUid: subscriptionUid,
        paymentUid: paymentUid,
        nameCC: dataBillingInfo.name,
        address: address,
      };

      await client.mutate({
        mutation: UPDATE_BILLING_INFO_MUTATION,
        variables: mutationVariables,
      });

      dispatch(
        setBillingInfo({ subscriptionUid, billingInfo: dataBillingInfo })
      );
      cb?.();

      dispatch(setNotification(t('notificationTexts.info.billingInfo')));
      dispatch(setIsClicked(false));
    } catch (err) {
      dispatch(setIsClicked(false));
      dispatch(setIsButtonloading(false));
      dispatch(setError(err.message));
      return rejectWithValue(t('notificationTexts.err.billing'));
    }
  }
);
